/**
 * useCopilotTracking — one line per screen
 * ═══════════════════════════════════════════════════════════════
 *
 * 19 of the 22 catalogue actions were never emitted, so any trigger rule built
 * on them could not fire. The provider can only observe navigation on its own —
 * it cannot know that a save failed or a form was abandoned.
 *
 * This makes reporting one call rather than boilerplate:
 *
 *     const track = useCopilotTracking();
 *     track.saveError({ entity: 'invoice', reason: err.message });
 *
 * Every method is a no-op when no copilot is bound to the route, or when the
 * person has opted out of recording — a screen does not have to check either.
 */
import { useMemo } from 'react';
import { useCopilot } from '@components/copilot/CopilotProvider';

export default function useCopilotTracking() {
  const { trackAction, boundAgent, recording } = useCopilot();

  return useMemo(() => {
    // Silently drop when nothing is listening. A screen should never carry an
    // `if (copilotEnabled)` around its own logic.
    const emit = (name) => (payload = {}) => {
      if (!boundAgent || !recording) return;
      try { trackAction(name, payload); } catch { /* never break the page */ }
    };

    return {
      // Navigation
      tabSwitch: emit('tab_switch'),

      // Records
      recordOpen: emit('record_open'),
      recordCreate: emit('record_create'),
      recordDelete: emit('record_delete'),
      recordDuplicate: emit('record_duplicate'),

      // Editing
      fieldEdit: emit('field_edit'),
      fieldClear: emit('field_clear'),
      formOpen: emit('form_open'),
      formAbandon: emit('form_abandon'),
      saveSuccess: emit('save_success'),
      saveError: emit('save_error'),
      undo: emit('undo'),

      // Search
      searchRun: emit('search_run'),
      searchEmpty: emit('search_empty'),
      filterChange: emit('filter_change'),

      // Attention
      errorShown: emit('error_shown'),
      validationFail: emit('validation_fail'),
      helpOpen: emit('help_open'),
      retry: emit('retry'),

      /** Escape hatch for a name not in the list. */
      raw: (name, payload) => emit(name)(payload),
    };
  }, [trackAction, boundAgent, recording]);
}
